import React, { useCallback, useEffect, useRef } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { Box } from '@mui/material';

import { parseNoteToBlocks, serializeBlocks } from './serialization';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Same minimal schema as the editor
const { codeBlock: _codeBlock, ...blockSpecs } = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const schema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

interface AppointmentNoteViewerProps {
  value: string;
  onCheckboxToggle?: (updatedValue: string) => void;
}

export const AppointmentNoteViewer: React.FC<AppointmentNoteViewerProps> = ({
  value,
  onCheckboxToggle,
}) => {
  const editor = useCreateBlockNote({ schema });

  const onCheckboxToggleRef = useRef(onCheckboxToggle);
  onCheckboxToggleRef.current = onCheckboxToggle;
  const localChangeRef = useRef(false);
  const lastValueRef = useRef(value);
  const initializedRef = useRef(false);

  // Initialize editor content (once)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!value) return;
    void (async () => {
      try {
        const blocks = await parseNoteToBlocks(editor, value);
        if (blocks.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          editor.replaceBlocks(editor.document, blocks);
        }
      } catch (err) {
        console.error('Error initializing appointment note viewer:', err);
      }
    })();
  }, [editor, value]);

  // Sync external value changes (e.g. after save completes)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (localChangeRef.current) {
      localChangeRef.current = false;
      return;
    }
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;

    void (async () => {
      try {
        const blocks = await parseNoteToBlocks(editor, value || '');
        if (blocks.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          editor.replaceBlocks(editor.document, blocks);
        }
      } catch (err) {
        console.error('Error syncing appointment note viewer:', err);
      }
    })();
  }, [editor, value]);

  // Intercept checkbox clicks on the read-only editor
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!onCheckboxToggleRef.current) return;

      const target = e.target as HTMLElement;
      const checkboxEl = target.closest(
        '[data-content-type="checkListItem"] input[type="checkbox"]',
      );
      if (!checkboxEl) return;

      const blockEl = checkboxEl.closest('[data-id]');
      const blockId = blockEl?.getAttribute('data-id');
      if (!blockId) return;

      const block = editor.getBlock(blockId);
      if (!block || block.type !== 'checkListItem') return;

      // Toggle checked state programmatically
      const currentChecked = (block.props as Record<string, unknown>).checked;
      editor.updateBlock(block, {
        props: { checked: !currentChecked } as Record<string, boolean>,
      });

      // Serialize and notify parent
      const serialized = serializeBlocks(editor.document);
      localChangeRef.current = true;
      lastValueRef.current = serialized;
      onCheckboxToggleRef.current(serialized);
    },
    [editor],
  );

  return (
    <Box
      onClick={handleClick}
      sx={{
        '& .bn-editor': {
          fontSize: '0.875rem',
          padding: '0 8px',
        },
        '& [data-content-type="checkListItem"] input[type="checkbox"]': {
          cursor: 'pointer',
        },
      }}
    >
      <BlockNoteView
        editor={editor}
        editable={false}
        theme="light"
        formattingToolbar={false}
        sideMenu={false}
        slashMenu={false}
      />
    </Box>
  );
};
